import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { dayjs, duration } from 'date';
import { logger } from 'monitor';

import {
  useApiEvidenceV1DataPartialUpdate,
  useApiQualityExternalLoadsV1ExternalLoadsEvidenceUploadCreate,
  useApiQualityExternalLoadsV1ExternalLoadsEvidenceUploadUpdate,
  useApiQualityExternalUploadV1EvidenceUploadCreate,
  useApiQualityExternalUploadV1EvidenceUploadUpdate,
  useApiQualitySessionsV2TradeCompanySessionsEvidenceUploadCreate,
  useApiQualitySessionsV2TradeCompanySessionsEvidenceUploadUpdate,
} from '../../client/api/api';
import { EvidenceTypeEnum, type GenerateUploadUrlResponse, type ProcessUploadResponse } from '../../schemas';
import { useUploadFile } from '../useUploadFile/useUploadFile';

dayjs.extend(duration);
export const prepareUploadMetrics = (
  photo: File,
  startTime: number,
  uploadStartTime: number,
  fileUploadedTime: number,
  fileProcessingDone: number
) => {
  const fullProcessTime = dayjs.duration(fileProcessingDone - startTime, 'milliseconds').asSeconds();
  const uploadFileDurationMs = Math.max(fileUploadedTime - uploadStartTime, 1);
  const uploadFileTime = dayjs.duration(uploadFileDurationMs, 'milliseconds').asSeconds();
  const photoSizeInBits = photo.size * 8;
  return {
    full_process_time: fullProcessTime,
    // bytes
    size: photo.size,
    upload_file_time: uploadFileTime,
    // bps
    upload_speed: photoSizeInBits / uploadFileTime,
  };
};

export type UploadEvidenceData = {
  photo: File;
  onProgress?: (progress: number) => void;
  parentId: string;
  model?: string;
  startTime: number;
  evidenceType?: EvidenceTypeEnum;
  metadata?: Record<string, unknown>;
  processAsync?: boolean;
};

export type EvidenceUploadMode = 'trade-company-session' | 'external-evidence' | 'external-load';

export type CreateEvidenceUploadTargetData = {
  parentId: string;
  photo: File;
};

export type UploadEvidenceBytesData = {
  onProgress?: (progress: number) => void;
  photo: File;
  uploadUrl: string;
};

export type FinalizeEvidenceUploadData = {
  evidenceType?: EvidenceTypeEnum;
  fileId: string;
  metadata?: Record<string, unknown>;
  model?: string;
  parentId: string;
  processAsync?: boolean;
};

export type EvidenceUploadClient = {
  createUploadTarget: (data: CreateEvidenceUploadTargetData) => Promise<GenerateUploadUrlResponse>;
  finalizeUpload: (data: FinalizeEvidenceUploadData) => Promise<ProcessUploadResponse>;
  trackUploadMetrics: (data: {
    analysis: ProcessUploadResponse;
    metrics: ReturnType<typeof prepareUploadMetrics>;
  }) => void;
  uploadBytes: (data: UploadEvidenceBytesData) => Promise<void>;
};

export const useEvidenceUploadClient = (mode: EvidenceUploadMode): EvidenceUploadClient => {
  const { mutateAsync: uploadFile } = useUploadFile();
  const { mutateAsync: uploadData } = useApiEvidenceV1DataPartialUpdate();

  const { mutateAsync: generateTradeCompanySessionEvidenceUploadUrl } =
    useApiQualitySessionsV2TradeCompanySessionsEvidenceUploadCreate();
  const { mutateAsync: processTradeCompanySessionEvidenceFile } =
    useApiQualitySessionsV2TradeCompanySessionsEvidenceUploadUpdate();

  const { mutateAsync: generateExternalEvidenceUploadUrl } = useApiQualityExternalUploadV1EvidenceUploadCreate();
  const { mutateAsync: processExternalEvidenceFile } = useApiQualityExternalUploadV1EvidenceUploadUpdate();

  const { mutateAsync: generateExternalLoadEvidenceUploadUrl } =
    useApiQualityExternalLoadsV1ExternalLoadsEvidenceUploadCreate();
  const { mutateAsync: processExternalLoadEvidenceFile } =
    useApiQualityExternalLoadsV1ExternalLoadsEvidenceUploadUpdate();

  const generateUploadUrl = (photo: File, parentId: string) => {
    logger.debug('generateUploadUrl', { mode, parentId, photo });

    if (mode === 'trade-company-session') {
      return generateTradeCompanySessionEvidenceUploadUrl({
        data: { content_type: photo.type },
        tradeCompanySessionId: Number(parentId),
      });
    }

    if (mode === 'external-evidence') {
      return generateExternalEvidenceUploadUrl({
        data: { content_type: photo.type },
        uuid: parentId,
      });
    }

    if (mode === 'external-load') {
      return generateExternalLoadEvidenceUploadUrl({
        data: { content_type: photo.type },
        externalLoadId: Number(parentId),
      });
    }

    throw new Error('Invalid evidence mode');
  };

  const registerEvidence = (
    file_id: string,
    parentId: string,
    model?: string,
    evidenceType: EvidenceTypeEnum = EvidenceTypeEnum.BP,
    metadata?: Record<string, unknown>,
    process_async?: boolean
  ) => {
    logger.debug('registerEvidence', { evidenceType, file_id, mode, model, parentId });

    if (mode === 'trade-company-session') {
      return processTradeCompanySessionEvidenceFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            origin: 'trade-company-session',
          },
          model,
          process_async,
        },
        tradeCompanySessionId: Number(parentId),
      });
    }

    if (mode === 'external-evidence') {
      return processExternalEvidenceFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            origin: 'external-evidence',
          },
          model,
          process_async,
        },
        uuid: parentId,
      });
    }

    if (mode === 'external-load') {
      return processExternalLoadEvidenceFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            origin: 'external-load',
          },
          model,
          process_async,
        },
        externalLoadId: Number(parentId),
      });
    }

    throw new Error('Invalid evidence mode');
  };

  const createUploadTarget = ({ parentId, photo }: CreateEvidenceUploadTargetData) =>
    generateUploadUrl(photo, parentId);

  const uploadBytes = ({ onProgress, photo, uploadUrl }: UploadEvidenceBytesData) =>
    uploadFile({
      file: photo,
      headers: { 'content-type': photo.type },
      onProgress,
      url: uploadUrl,
    });

  const finalizeUpload = ({
    evidenceType,
    fileId,
    metadata,
    model,
    parentId,
    processAsync,
  }: FinalizeEvidenceUploadData) => registerEvidence(fileId, parentId, model, evidenceType, metadata, processAsync);

  const trackUploadMetrics: EvidenceUploadClient['trackUploadMetrics'] = ({ analysis, metrics }) => {
    if (!analysis.image?.id) {
      return;
    }

    uploadData({ data: metrics, id: analysis.image.id });
  };

  return {
    createUploadTarget,
    finalizeUpload,
    trackUploadMetrics,
    uploadBytes,
  };
};

export const useUploadEvidence = (
  mode: EvidenceUploadMode,
  mutationOptions?: UseMutationOptions<ProcessUploadResponse, unknown, UploadEvidenceData, unknown>
) => {
  const uploadClient = useEvidenceUploadClient(mode);

  return useMutation({
    ...mutationOptions,
    mutationFn: async (data: UploadEvidenceData) => {
      const { photo, onProgress, parentId, model, metadata, processAsync } = data;
      const { upload_url, file_id } = await uploadClient.createUploadTarget({ parentId, photo });

      logger.info('Uploading evidence', {
        evidenceType: data.evidenceType,
        fileName: photo.name,
        parentId,
        upload_url,
      });

      const uploadStartTime = Date.now();

      await uploadClient.uploadBytes({
        onProgress,
        photo,
        uploadUrl: upload_url,
      });

      logger.info('Evidence uploaded; analysing', {
        evidenceType: data.evidenceType,
        fileName: photo.name,
        parentId,
        upload_url,
      });

      const fileUploadedTime = Date.now();
      const analysis = await uploadClient.finalizeUpload({
        evidenceType: data.evidenceType,
        fileId: file_id,
        metadata,
        model,
        parentId,
        processAsync,
      });
      const fileProcessingDone = Date.now();
      const metrics = prepareUploadMetrics(
        photo,
        data.startTime,
        uploadStartTime,
        fileUploadedTime,
        fileProcessingDone
      );

      logger.info('Evidence analysis completed', {
        analysis,
        evidenceType: data.evidenceType,
        fileName: photo.name,
        metrics,
        parentId,
        upload_url,
      });

      uploadClient.trackUploadMetrics({ analysis, metrics });

      return analysis;
    },
  });
};
